/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `UserRoles` for the Caintra Connect frontend.
 * @module pages/UserRoles
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { apiMessageOrGeneric } from "../utils/genericErrorMessages.js";
import {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
} from "../utils/apiConstants.js";
import { logger, sanitizeError } from "../utils/logger.js";
import styles from "../styles/UserRoles.module.css";

import LanguageSelector from "../components/LanguageSelector/index.jsx";
import NotificationBell from "../components/NotificationBell/index.jsx";
import { AppContext } from "../context/AppContext";
import { getToken, refreshToken, signOut } from "../utils/common.js";
import { ReactComponent as EditIcon } from "../assets/icons/Edit.svg";
import { ReactComponent as SearchIcon } from "../assets/icons/Search.svg";
import { ReactComponent as UserIcon } from "../assets/icons/UserRoles/UserIcon.svg";

import { styled } from "@mui/material/styles";
import { SvgIcon, Switch } from "@mui/material";
import ContactPageOutlinedIcon from "@mui/icons-material/ContactPageOutlined";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { use, useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import RoleCreationModal from "../modals/RoleCreationModal/index.jsx";
import { canAccessRoles, isAdminUser } from "../utils/accessControl";
import useRequireAccess from "../hooks/useRequireAccess";

const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 64,
  height: 32,
  padding: 0,
  display: "flex",
  "&:active": {
    "& .MuiSwitch-thumb": {
      width: 30,
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      transform: "translateX(9px)",
    },
  },
  "& .MuiSwitch-switchBase": {
    padding: 4,
    "&.Mui-checked": {
      transform: "translateX(32px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: "#1D5AE8",
        ...theme.applyStyles("dark", {
          backgroundColor: "#177ddc",
        }),
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
    width: 24,
    height: 24,
    borderRadius: 999,
    transition: theme.transitions.create(["width"], {
      duration: 200,
    }),
  },
  "& .MuiSwitch-track": {
    borderRadius: "999px",
    opacity: 1,
    backgroundColor: "rgba(0,0,0,.25)",
    boxSizing: "border-box",
    ...theme.applyStyles("dark", {
      backgroundColor: "rgba(255,255,255,.35)",
    }),
  },
}));

/** API error message when the auth token has expired */
/** Content-Type header value for JSON */

/**
 * User roles management page.
 * @returns {JSX.Element}
 */
const UserRoles = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { allowed, user } = useRequireAccess(canAccessRoles);
  const canMutateRoles = isAdminUser(user);
  const { changePermissions } = use(AppContext);
  const [canEdit, setCanEdit] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [rolesData, setRolesData] = useState([]);
  const [originalRolesData, setOriginalRolesData] = useState([]);
  const [expandedRoles, setExpandedRoles] = useState(new Set());

  useEffect(() => {
    if (getToken()) {
      getData();
    }
  }, []);

  const getData = async () => {
    const options = {
      method: "GET",
      headers: {
        "Content-Type": CONTENT_TYPE_JSON,
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    };

    try {
      const userId = localStorage.getItem("userId");
      const response = await fetch(
        `${process.env.REACT_APP_ROOT_API}permission/${userId}`,
        options,
      );
      const roleData = await response.json();
      if (roleData?.message === TOKEN_EXPIRED_MESSAGE) {
        const newTokens = await refreshToken();
        if (!newTokens) {
          await signOut();
          navigate("/");
          return;
        }
        await getData();
        return;
      }
      if (response.ok && roleData?.permisos != null) {
        changePermissions(roleData.permisos);
      }
      await getUserRoles();
    } catch (error) {
      logger.error("Error", { err: sanitizeError(error) });
    }
  };

  const getUserRoles = async () => {
    const options = {
      method: "GET",
      headers: {
        "Content-Type": CONTENT_TYPE_JSON,
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_ROOT_API}permission`,
        options,
      );
      const data = await response.json();
      if (data?.message === TOKEN_EXPIRED_MESSAGE) {
        const newTokens = await refreshToken();
        if (!newTokens) {
          await signOut();
          navigate("/");
          return;
        }
        await getUserRoles();
        return;
      }
      if (!response.ok || !Array.isArray(data)) {
        setRolesData([]);
        setOriginalRolesData([]);
        return;
      }
      setRolesData(data);
      setOriginalRolesData(data);
    } catch (error) {
      logger.error("Error", { err: sanitizeError(error) });
    }
  };

  const onSwitchChange = () => {
    if (hasChanged) {
      setRolesData(originalRolesData);
      setHasChanged(false);
    }
    setCanEdit((prev) => !prev);
  };

  const checkUserRole = (roleIndex, permisoIndex) => {
    const roleValue =
      rolesData[roleIndex].permisos[permisoIndex][
        Object.keys(rolesData[roleIndex].permisos[permisoIndex])[0]
      ];
    const newRolesData = rolesData.map((role, rIdx) => {
      if (rIdx === roleIndex) {
        return {
          ...role,
          permisos: role.permisos.map((permiso, pIdx) => {
            if (pIdx === permisoIndex) {
              return {
                ...permiso,
                [Object.keys(permiso)[0]]: !roleValue,
              };
            }
            return permiso;
          }),
        };
      }
      return role;
    });

    setHasChanged(true);
    setRolesData(newRolesData);
  };

  const updateRole = async () => {
    if (!canMutateRoles) {
      return;
    }
    const authToken = getToken();
    if (!authToken) {
      return;
    }
    try {
      const options = {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(rolesData),
      };

      const response = await fetch(
        `${process.env.REACT_APP_ROOT_API}permission`,
        options,
      );
      const data = await response.json();
      if (data?.message === TOKEN_EXPIRED_MESSAGE) {
        const newTokens = await refreshToken();
        if (!newTokens) {
          await signOut();
          navigate("/");
          return;
        }
        await updateRole();
        return;
      }

      if (!response.ok) {
        throw new Error(apiMessageOrGeneric(data?.message));
      }

      setHasChanged(false);
      setCanEdit(false);
    } catch (error) {
      logger.error("Error:", { err: sanitizeError(error) });
    }
  };

  const handleCreateUserRole = (newRole) => {
    if (!canMutateRoles) {
      return;
    }
    setRolesData((prevRoles) => [...prevRoles, newRole]);
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const isTablet = () => {
    return window.innerWidth >= 768 && window.innerWidth <= 1023;
  };

  const isMediumMobile = () => {
    return window.innerWidth >= 481 && window.innerWidth <= 767;
  };

  const isSmallMobile = () => {
    return window.innerWidth <= 480;
  };

  const shouldUseSmallLayout = () => {
    return isTablet() || isMediumMobile() || isSmallMobile();
  };

  const toggleRoleExpansion = (roleIndex) => {
    setExpandedRoles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(roleIndex)) {
        newSet.delete(roleIndex);
      } else {
        newSet.add(roleIndex);
      }
      return newSet;
    });
  };

  if (!allowed) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.languages}>
          <NotificationBell navigate={navigate} />
          <LanguageSelector />
        </div>

        {/* Page title and search bar */}
        <div className={styles.headerInfoContainer}>
          <div className={styles.headerInfo}>
            <SvgIcon sx={{ fontSize: "32px" }} viewBox="0 0 24 24">
              <defs>
                <linearGradient
                  id="gradient1"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#4eabe7" />
                  <stop offset="100%" stopColor="#2f59df" />
                </linearGradient>
              </defs>
              <ContactPageOutlinedIcon sx={{ fill: "url(#gradient1)" }} />
            </SvgIcon>
            <h1 className={styles.title}>{t("pages.roles.header")}</h1>
          </div>
          <div className={styles.searchSection}>
            <div className={styles.searchBarContainer}>
              <div className={styles.searchBar}>
                <div style={{ position: "absolute", left: "1%" }}>
                  <SvgIcon
                    sx={{ fontSize: "16px", color: "#3B3B3D" }}
                    component={SearchIcon}
                  />
                </div>
                <input
                  placeholder={t("pages.roles.searchText")}
                  type="search"
                  value={searchQuery}
                  onChange={handleSearch}
                />
              </div>
              <div className={styles.searchBarIcon}>
                <SvgIcon
                  sx={{ height: "1.3rem", color: "#FCFCFC" }}
                  component={SearchIcon}
                />
              </div>
            </div>
            {canMutateRoles && (
              <div
                className={styles.newRoleButton}
                onClick={() => setModalOpen(true)}
              >
                <SvgIcon component={AddIcon} fontSize="small" />
                {isMediumMobile() || isSmallMobile()
                  ? ""
                  : t("pages.roles.newRole")}
              </div>
            )}
            <RoleCreationModal
              open={modalOpen}
              onClose={() => setModalOpen(false)}
              onCreateRole={handleCreateUserRole}
            />
          </div>
        </div>
      </div>

      <div className={styles.mainContent}>
        <div className={styles.buttonsContainer}>
          {canMutateRoles && (
            <div className={styles.switchContainer}>
              <span>{t("pages.roles.activate")}</span>
              <AntSwitch
                inputProps={{ "aria-label": t("pages.roles.activate") }}
                checked={canEdit}
                onChange={() => onSwitchChange()}
                className={styles.switch}
              />
            </div>
          )}

          {hasChanged && (
            <div className={styles.saveButton} onClick={() => updateRole()}>
              <SvgIcon
                sx={{ fontSize: "16px", color: "#1D5AE8" }}
                component={EditIcon}
              />
              {t("pages.roles.save")}
            </div>
          )}
        </div>
        <div className={styles.tableContainer}>
          {shouldUseSmallLayout() ? (
            <div className={styles.tabletTableContainer}>
              <div className={styles.tabletHeader}>
                <div className={styles.tabletHeaderCell}>
                  <UserIcon className={styles.userIcon} />
                  {t("pages.roles.memberType")}
                </div>
              </div>
              <div className={styles.tabletBody}>
                {rolesData.length > 0 &&
                  rolesData.map((role, roleIndex) => (
                    <div key={roleIndex} className={styles.tabletRoleRow}>
                      <div className={styles.tabletRoleHeader}>
                        <div className={styles.tabletRoleName}>
                          {role.nombre}
                        </div>
                        <div
                          className={`${styles.tabletExpandButton} ${
                            expandedRoles.has(roleIndex) ? styles.expanded : ""
                          }`}
                          onClick={() => toggleRoleExpansion(roleIndex)}
                        >
                          <ExpandMoreIcon />
                        </div>
                      </div>
                      <div
                        className={`${styles.tabletPermissionsContainer} ${
                          expandedRoles.has(roleIndex)
                            ? styles.expanded
                            : styles.collapsed
                        }`}
                      >
                        <div className={styles.tabletPermissionsGrid}>
                          {role.permisos.map((permiso, idx) => {
                            const permisoKey = Object.keys(permiso)[0];
                            let permissionLabel = "";

                            switch (permisoKey) {
                              case "viewDirectory":
                                permissionLabel = t("pages.roles.directory");
                                break;
                              case "viewCompanyCatalog":
                                permissionLabel = t("pages.roles.companies");
                                break;
                              case "viewUsers":
                                permissionLabel = t("pages.roles.users");
                                break;
                              case "viewRoles":
                                permissionLabel = t("pages.roles.roles");
                                break;
                              case "viewCategories":
                                permissionLabel = t("pages.roles.categories");
                                break;
                              case "viewRequirements":
                                permissionLabel = t("pages.roles.requirements");
                                break;
                              case "viewEvents":
                                permissionLabel = t("pages.roles.events");
                                break;
                              case "manageEvents":
                                permissionLabel = t("pages.roles.manageEvents");
                                break;
                              case "manageBusinessEvents":
                                permissionLabel = t(
                                  "pages.roles.manageBusinessEvents",
                                );
                                break;
                              case "manageB2BEvents":
                                permissionLabel = t(
                                  "pages.roles.manageB2BEvents",
                                );
                                break;
                              case "manageVirtualEvents":
                                permissionLabel = t(
                                  "pages.roles.manageVirtualEvents",
                                );
                                break;
                              case "manageBreakfastEvents":
                                permissionLabel = t(
                                  "pages.roles.manageBreakfastEvents",
                                );
                                break;
                              case "viewCustomNotifications":
                                permissionLabel = t(
                                  "pages.roles.notifications",
                                );
                                break;
                              case "viewEventAttendees":
                                permissionLabel = t("pages.roles.attendance");
                                break;
                              case "viewChat":
                                permissionLabel = t("pages.roles.chat");
                                break;
                              case "editHomepage":
                                permissionLabel = t("pages.roles.homepage");
                                break;
                              default:
                                permissionLabel = permisoKey;
                            }

                            return (
                              <div
                                key={idx}
                                className={styles.tabletPermissionItem}
                              >
                                <div className={styles.tabletPermissionLabel}>
                                  {permissionLabel}
                                </div>
                                <div className={styles.checkboxContainer}>
                                  <input
                                    className={styles.checkbox}
                                    type="checkbox"
                                    id={`checkbox-${roleIndex}-${idx}`}
                                    checked={Object.values(permiso)[0]}
                                    onClick={() =>
                                      checkUserRole(roleIndex, idx)
                                    }
                                    disabled={!canEdit}
                                  />
                                  <div className={styles.customCheckbox}></div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ backgroundColor: "#D2DEFA" }}>
                    {t("pages.roles.memberType")}
                  </th>
                  <th>{t("pages.roles.directory")}</th>
                  <th>{t("pages.roles.companies")}</th>
                  <th>{t("pages.roles.users")}</th>
                  <th>{t("pages.roles.roles")}</th>
                  <th>{t("pages.roles.categories")}</th>
                  <th>{t("pages.roles.requirements")}</th>
                  <th>{t("pages.roles.events")}</th>
                  <th>{t("pages.roles.manageEvents")}</th>
                  <th>{t("pages.roles.manageBusinessEvents")}</th>
                  <th>{t("pages.roles.manageB2BEvents")}</th>
                  <th>{t("pages.roles.manageVirtualEvents")}</th>
                  <th>{t("pages.roles.manageBreakfastEvents")}</th>
                  <th>{t("pages.roles.attendance")}</th>
                  <th>{t("pages.roles.notifications")}</th>
                  <th>{t("pages.roles.chat")}</th>
                  <th>{t("pages.roles.homepage")}</th>
                </tr>
              </thead>
              <tbody>
                {rolesData.length > 0 &&
                  rolesData.map((role, roleIndex) => (
                    <tr key={roleIndex}>
                      <td>{role.nombre}</td>
                      {role.permisos.map((permiso, idx) => (
                        <td key={idx}>
                          <div className={styles.checkboxContainer}>
                            <input
                              className={styles.checkbox}
                              type="checkbox"
                              id="checkbox"
                              checked={Object.values(permiso)[0]}
                              onClick={() => checkUserRole(roleIndex, idx)}
                              disabled={!canEdit}
                            />
                            <div className={styles.customCheckbox}></div>
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserRoles;
